import { ApplicantTabNavigation } from "@components/layout/ApplicantTabNavigation";
import { ApplicationHeader } from "@components/layout/ApplicationHeader";
import { getApplicationHeaderProps } from "@lib/utils/applicationHeaderHelpers";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import {
  useState,
  useRef,
  useEffect,
  type MouseEvent,
} from "react";
import {
  PenTool,
  ArrowRight,
  X,
  Save,
  Info,
  Upload,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Loader2,
  IdCard,
  FileText,
  FileSignature,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectApplicationData,
  selectApplicationLoading,
  selectIsReadOnly,
  fetchApplicationById,
  fetchApplications,
  clearError,
} from "@/features/applicant/applicationSlice";
import { SSNInput } from "@components/ui/SSNInput";
import { updateApplication } from "@/features/applicant/applicationSlice";
import apiClient from "@/lib/api/apiClient";
import {
  fetchCreditBureauActive,
  fetchCreditBureauDetails,
  type CreditBureauConfig,
} from "@/lib/api/applicationService";
import {
  residentTabValidation,
  type ApplicationFileAttachment,
  type UpdateApplicationRequest,
} from "@/types/application";
import { normalizeGovIdAttachments } from "@/features/applicant/utils/applicationDataHelpers";
import { Thumbnail } from "@components/file-preview";
import {
  isImageFile,
  isDocumentFile,
  isHeicOrHeifFile,
  openFilePreview,
  openFilePreviewByPath,
  generateFilePreviewUrl,
  buildStreamFileUrl,
  ensureFileToken,
} from "@/lib/utils/filePreview";
import { convertHeicBlobToJpegUrl } from "@/lib/utils/heicConversion";
import { showError } from "@/lib/utils/notifications";
import {
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ACCEPT,
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR,
  isAcceptedIncomeOrIdDocumentFile,
} from "@/lib/utils/acceptedDocumentFile";
import { useNavigate, useParams } from "react-router-dom";
import {
  getTabPathWithApplicantId,
  getTabNavigationOptions,
} from "@lib/utils/applicationNavigation";
import { useTabValidation, useTabAutoSave } from "@/features/applicant/context/TabValidationContext";
import { ValidationModal } from "@components/ui/ValidationModal";
import { applicantValidationModalProps } from "@/features/applicant/constants/applicantDialogProps";
import { SignatureDisclosureModal } from "@/features/applicant/components/SignatureDisclosureModal";
import type { DisclosureLinkId } from "@/features/applicant/types/pages";
import { sendAnalytics } from "@/lib/utils/analytics";

/** Angular parity (resident-application-add `selectFileUpload`): government ID max 5 MB. */
const GOV_ID_MAX_FILE_SIZE_MB = 5;
const GOV_ID_FILE_ACCEPT = ACCEPTED_INCOME_AND_ID_DOCUMENT_ACCEPT;

export function Signature() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();
  const applicationData = useAppSelector(selectApplicationData);
  const isLoading = useAppSelector(selectApplicationLoading);
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const { registerValidator, unregisterValidator } = useTabValidation();

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;
  const [hasFetched, setHasFetched] = useState(false);
  const [link1Checked, setLink1Checked] = useState(false);
  const [link2Checked, setLink2Checked] = useState(false);
  const [link3Checked, setLink3Checked] = useState(false);
  const [ssnDigits, setSsnDigits] = useState<string>("");
  const [governmentIdType, setGovernmentIdType] = useState<
    "drivers-license" | "passport" | null
  >("drivers-license");
  const [driversLicenseFront, setDriversLicenseFront] = useState<File | null>(
    null,
  );
  const [driversLicenseBack, setDriversLicenseBack] = useState<File | null>(
    null,
  );
  const [passportImage, setPassportImage] = useState<File | null>(null);
  // Object URLs for previewing newly uploaded files
  const [driversLicenseFrontUrl, setDriversLicenseFrontUrl] = useState<
    string | null
  >(null);
  const [driversLicenseBackUrl, setDriversLicenseBackUrl] = useState<
    string | null
  >(null);
  const [passportImageUrl, setPassportImageUrl] = useState<string | null>(null);
  const [signature, setSignature] = useState<string>("");

  const [signatureError, setSignatureError] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingExit, setIsSavingExit] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [uploadingGovId, setUploadingGovId] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalMessage, setValidationModalMessage] = useState("");
  const [validationShowIcon, setValidationShowIcon] = useState(true);
  // Checkbox matching Angular: access_statement (uc_optin state kept for API payload)
  const [ucOptIn, setUcOptIn] = useState(false);
  const [accessStatementChecked, setAccessStatementChecked] = useState(false);
  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<{
    allDocumentsChecked?: string;
    governmentIdType?: string;
    governmentIdFiles?: string;
    signature?: string;
    accessStatement?: string;
    ssn?: string;
  }>({});
  const [validateForm, setValidateForm] = useState(false);

  const [creditBureauConfig, setCreditBureauConfig] = useState<CreditBureauConfig | null>(null);
  const isImpersonate = !!localStorage.getItem("admin_token") || localStorage.getItem("impersonate") === "true";

  useEffect(() => {
    async function loadCreditBureauConfig() {
      try {
        const cbActive = await fetchCreditBureauActive();
        let config = cbActive;
        if (applicantId) {
          const cbDetails = await fetchCreditBureauDetails(applicantId);
          if (cbDetails?.active) config = cbDetails;
        }
        setCreditBureauConfig(config);
      } catch {
        /* keep null — falls back to hardcoded Equifax defaults */
      }
    }
    loadCreditBureauConfig();
  }, [applicantId]);

  const bureauName = creditBureauConfig?.details?.config_value || "Equifax Information Services, LLC";
  const bureauAddress = creditBureauConfig?.details
    ? `${creditBureauConfig.details.cb_addressline1 || ""}, ${creditBureauConfig.details.cb_city || ""}, ${creditBureauConfig.details.cb_state || ""} ${creditBureauConfig.details.cb_zipcode || ""}`
    : "P.O. Box 740256, Atlanta, GA 30374";
  const bureauPhone = creditBureauConfig?.details?.cb_telephone || "(866) 349-5191";
  const bureauFax = creditBureauConfig?.details?.cb_fax || "";
  const isEquifax = creditBureauConfig?.active === "Equifax" || !creditBureauConfig;
  const bureauWeb = isEquifax ? "https://www.equifax.com/personal/" : "";

  // Disclosure modal: show document in popup, scroll to acknowledge, then Accept checks outer checkbox
  const [disclosureModalLinkId, setDisclosureModalLinkId] = useState<
    DisclosureLinkId | null
  >(null);

  // Check if all required documents are checked (terms reading)
  const allDocumentsChecked = link1Checked && link2Checked && link3Checked;

  const openDisclosureModal = (linkId: DisclosureLinkId) => {
    setDisclosureModalLinkId(linkId);
  };

  const handleDisclosureLinkClick = (
    event: MouseEvent<HTMLButtonElement>,
    linkId: DisclosureLinkId,
  ) => {
    event.preventDefault();
    openDisclosureModal(linkId);
  };

  const closeDisclosureModal = () => {
    setDisclosureModalLinkId(null);
  };

  const handleDisclosureAccept = (linkId: DisclosureLinkId) => {
    if (linkId === 1) {
      setLink1Checked(true);
    } else if (linkId === 2) {
      setLink2Checked(true);
    } else {
      setLink3Checked(true);
    }
    if (fieldErrors.allDocumentsChecked) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.allDocumentsChecked;
        return next;
      });
    }
    closeDisclosureModal();
  };

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Function to handle signature name mismatch error (called when API returns error)
  const handleSignatureNameMismatch = async () => {
    setValidationModalMessage(APPLICANT_CONTENT.errors.signature.signatureMismatch);
    setValidationShowIcon(false);
    setShowValidationModal(true);

    // Prevent API calls when application is read-only
    if (isReadOnly) {
      return;
    }

    // Log the name override request to backend (for tracking)
    if (applicationData?.applicant_id) {
      try {
        await apiClient.post(
          `/api/name_override/${applicationData.applicant_id}`,
        );
        // No need to handle response - this is just for logging
      } catch {
        // Silently fail - this is just for tracking, don't show error to user
      }
    }
  };

  // Get expected signature from application data
  const getExpectedSignature = (): string => {
    if (!applicationData) return "";
    const firstName = applicationData.first_name || "";
    const lastName = applicationData.last_name || "";
    return `${firstName} ${lastName}`.trim();
  };

  // Validate signature matches first and last name according to business logic
  // Signature must match first name + last name (case-insensitive)
  const validateSignature = (value: string): boolean => {
    if (!applicationData || !value.trim()) return true; // If no application data or empty, allow

    const firstName = applicationData.first_name || "";
    const lastName = applicationData.last_name || "";

    if (!firstName || !lastName) return true; // If missing name data, allow

    const fullName = `${firstName} ${lastName}`.toLowerCase().trim();
    const signature = value.toLowerCase().trim();

    return signature === fullName;
  };

  // Listen for messages from notice pages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) {
        return;
      }

      const { type, linkType } = event.data;

      if (type === "linkOpened" || type === "linkClosed") {
        // When a link is opened or closed, check the corresponding checkbox
        if (linkType === "terms_fair") {
          setLink1Checked(true);
        } else if (linkType === "terms_credit") {
          setLink2Checked(true);
        } else if (linkType === "authorization_for_liberty") {
          setLink3Checked(true);
        }

        // Clear validation error - will be checked after state updates
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.allDocumentsChecked;
          return newErrors;
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Fetch application data on mount if not already loaded
  useEffect(() => {
    if (!applicationData?.applicant_id && !isLoading && !hasFetched) {
      setHasFetched(true);
      // Try to get applicant_id from storage
      const storedApplicantId = localStorage.getItem("applicant_id");
      if (storedApplicantId) {
        dispatch(fetchApplicationById(storedApplicantId));
      } else {
        dispatch(fetchApplications());
      }
    }
  }, [applicationData?.applicant_id, isLoading, hasFetched, dispatch]);

  // Note: fetchApplicationById is called by the parent ResidentApplication component,
  // which waits for it to complete before rendering this tab. No need to fetch here.

  // Load and autofill all existing data from application data (like About page does)
  useEffect(() => {
    if (applicationData) {
      const appData = applicationData;

      // Load SSN from application data - store as raw digits for SSNInput component
      // Check both security_number at root level (like in the API response)
      if (appData.security_number) {
        // Extract digits - handle both string and number formats
        const ssnString = String(appData.security_number);
        const digits = ssnString.replace(/\D/g, "");
        if (digits.length >= 9) {
          // Take first 9 digits if more than 9
          setSsnDigits(digits.slice(0, 9));
        } else if (digits.length > 0) {
          // If less than 9 digits, still set what we have
          setSsnDigits(digits);
        }
      }

      // Load signature from digital_signature (JSON string format)
      if (appData.digital_signature) {
        try {
          // digital_signature is stored as JSON string: "{\"signature\":\"Web Check\",\"signaturetype\":\"type1\"}"
          const signatureObj =
            typeof appData.digital_signature === "string"
              ? JSON.parse(appData.digital_signature)
              : appData.digital_signature;

          if (signatureObj && signatureObj.signature) {
            setSignature(signatureObj.signature);
          }
        } catch {
          // If parsing fails, try direct signature field
          if (appData.signature) {
            setSignature(appData.signature);
          }
        }
      } else if (appData.signature) {
        // Fallback to direct signature field
        setSignature(appData.signature);
      }

      // Load all_links_read - check all three links if it's set
      if (
        appData.all_links_read === "1" ||
        appData.all_links_read === 1 ||
        appData.all_links_read === true
      ) {
        setLink1Checked(true);
        setLink2Checked(true);
        setLink3Checked(true);
      }

      // Load uc_optin (Utility Concierge opt-in) from application data
      if (appData.uc_optin === "1" || appData.uc_optin === 1) {
        setUcOptIn(true);
      }

      // Load access_statement from application data
      if (appData.access_statement === "1" || appData.access_statement === 1) {
        setAccessStatementChecked(true);
      }

      // Load government ID type from goverment_id_type (note: API has typo "goverment" not "government")
      if (appData.goverment_id_type) {
        const govIdType = String(appData.goverment_id_type)
          .toLowerCase()
          .trim();
        if (govIdType.includes("driver") || govIdType.includes("license")) {
          setGovernmentIdType("drivers-license");
        } else if (govIdType === "passport" || govIdType.includes("passport")) {
          setGovernmentIdType("passport");
        }
      }

      // Load government ID attachment - handle both object and array formats
      const govIdAttachments = normalizeGovIdAttachments(
        appData.goverment_id_attachment,
      );
      if (govIdAttachments.length > 0 && !appData.goverment_id_type) {
        const fileType = govIdAttachments[0].type || "";
        if (fileType.toLowerCase().includes("passport")) {
          setGovernmentIdType("passport");
        } else {
          setGovernmentIdType("drivers-license");
        }
      }

      // Also check goverment_id_attachment_back - if it exists, it's likely drivers license
      const govIdAttachmentBack = appData.goverment_id_attachment_back;
      if (govIdAttachmentBack && !appData.goverment_id_type) {
        // If back attachment exists, it's definitely drivers license
        const backAttachments = normalizeGovIdAttachments(govIdAttachmentBack);
        if (backAttachments.length > 0) {
          setGovernmentIdType("drivers-license");
        }
      }
    }
  }, [applicationData]);  

  // Register validator for Tab Validation
  useEffect(() => {
    const validateSignatureTab = (): { isValid: boolean; message?: string } => {
      const errorMessages: string[] = [];

      // 1. Terms and Documents
      if (!link1Checked || !link2Checked || !link3Checked) {
        errorMessages.push(
          "Terms and Documents: Please read and agree to all terms and documents.",
        );
      }

      // 2. Government ID
      if (!governmentIdType) {
        errorMessages.push(
          "Identity Verification: Please select an ID type.",
        );
      } else {
        const appData = applicationData;
        const hasGovIdAttachment =
          normalizeGovIdAttachments(appData?.goverment_id_attachment).length >
          0;

        const hasGovIdAttachmentBack =
          governmentIdType === "passport" ||
          normalizeGovIdAttachments(appData?.goverment_id_attachment_back)
            .length > 0;

        const needsGovIdUpload =
          !hasGovIdAttachment ||
          (governmentIdType === "drivers-license" && !hasGovIdAttachmentBack);

        if (needsGovIdUpload) {
          if (governmentIdType === "drivers-license") {
            if (!driversLicenseFront || !driversLicenseBack) {
              errorMessages.push(
                "Identity Verification: Please upload both front and back images of your Driver's License.",
              );
            }
          } else if (governmentIdType === "passport") {
            if (!passportImage) {
              errorMessages.push(
                "Identity Verification: Please upload your Passport image.",
              );
            }
          }
        }
      }

      // 3. SSN
      if (!ssnDigits || ssnDigits.length !== 9) {
        errorMessages.push("SSN: Please provide a valid 9-digit SSN.");
      }

      // 4. Access Statement
      if (!accessStatementChecked) {
        errorMessages.push("Statements: Please agree to the access statement.");
      }

      // 5. Signature
      if (!signature.trim()) {
        errorMessages.push("Signature: Signature is required.");
      } else if (!validateSignature(signature)) {
        const expected = getExpectedSignature();
        errorMessages.push(`Signature: Signature must match "${expected}"`);
      }

      if (errorMessages.length > 0) {
        return {
          isValid: false,
          message: errorMessages.join("\n"),
        };
      }

      return { isValid: true };
    };

    registerValidator("signature", validateSignatureTab);
    return () => unregisterValidator("signature");
  }, [
    registerValidator,
    unregisterValidator,
    link1Checked,
    link2Checked,
    link3Checked,
    governmentIdType,
    applicationData,
    driversLicenseFront,
    driversLicenseBack,
    passportImage,
    ssnDigits,
    accessStatementChecked,
    signature,
    applicationData?.first_name,
    applicationData?.last_name,
  ]);

  const driversLicenseFrontRef = useRef<HTMLInputElement>(null);
  const driversLicenseBackRef = useRef<HTMLInputElement>(null);
  const passportRef = useRef<HTMLInputElement>(null);

  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);

  // Function to upload a single file immediately
  const uploadSingleFile = async (
    file: File,
    type: "front" | "back" | "passport",
  ) => {
    if (!applicationData?.applicant_id) return;

    // Set loading state
    if (type === "front") setUploadingFront(true);
    else if (type === "back") setUploadingBack(true);
    else setUploadingPassport(true);

    try {
      const formData = new FormData();
      // Always send the government ID type
      formData.append(
        "goverment_id_type",
        governmentIdType === "drivers-license" ? "Drivers License" : "Passport",
      );

      // Append the specific file being uploaded
      if (type === "front") {
        formData.append("goverment_id_attachment", file);
      } else if (type === "back") {
        formData.append("goverment_id_attachment_back", file);
      } else if (type === "passport") {
        formData.append("goverment_id_attachment", file);
      }

      const response = await apiClient.post(
        `/api/resident_application/${applicationData.applicant_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const responseData = response.data;
      if (responseData && responseData.success === false) {
        throw new Error(responseData.message || "Failed to upload document.");
      }

      // Fetch updated data to reflect the change
      await dispatch(
        fetchApplicationById(applicationData.applicant_id),
      ).unwrap();

      // Clear the local object URL since we now have the server version (via fetch)
      // and we want to show the server version (which comes from applicationData)
      if (type === "front" && driversLicenseFrontUrl) {
        URL.revokeObjectURL(driversLicenseFrontUrl);
        setDriversLicenseFrontUrl(null);
        setDriversLicenseFront(null);
      } else if (type === "back" && driversLicenseBackUrl) {
        URL.revokeObjectURL(driversLicenseBackUrl);
        setDriversLicenseBackUrl(null);
        setDriversLicenseBack(null);
      } else if (type === "passport" && passportImageUrl) {
        URL.revokeObjectURL(passportImageUrl);
        setPassportImageUrl(null);
        setPassportImage(null);
      }

      setSaveMessage({
        type: "success",
        text: "Document uploaded successfully!",
      });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: unknown) {
      let errorMessage = "Failed to upload document.";
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setSaveMessage({ type: "error", text: errorMessage });
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      if (type === "front") setUploadingFront(false);
      else if (type === "back") setUploadingBack(false);
      else setUploadingPassport(false);
    }
  };

  const handleFileUpload = async (
    file: File | null,
    type: "front" | "back" | "passport",
  ) => {
    // Restrict to PDF / JPG / JPEG / PNG / HEIC (not image/* / all docs).
    if (file && !isAcceptedIncomeOrIdDocumentFile(file)) {
      showError(ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR);
      return;
    }
    // Angular parity (selectFileUpload): reject files over 5 MB at selection — before any
    // upload — with the same error toast (`appService.notify(..., 0)`). Angular blocks the
    // upload outright here. A toast is used because the inline saveMessage banner only
    // renders success states.
    if (file && file.size / 1024 / 1024 > GOV_ID_MAX_FILE_SIZE_MB) {
      showError(`File Size Exceeds Allowable Limit of ${GOV_ID_MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    // Helper: create a displayable blob URL (converts HEIC→JPEG so the browser can render it)
    const createPreviewUrl = async (f: File): Promise<string> => {
      if (isHeicOrHeifFile(f.name)) {
        return convertHeicBlobToJpegUrl(f);
      }
      return URL.createObjectURL(f);
    };

    // Clean up previous object URL if exists
    if (type === "front") {
      if (driversLicenseFrontUrl) URL.revokeObjectURL(driversLicenseFrontUrl);
      setDriversLicenseFront(file);
      setDriversLicenseFrontUrl(file ? await createPreviewUrl(file) : null);
    } else if (type === "back") {
      if (driversLicenseBackUrl) URL.revokeObjectURL(driversLicenseBackUrl);
      setDriversLicenseBack(file);
      setDriversLicenseBackUrl(file ? await createPreviewUrl(file) : null);
    } else {
      if (passportImageUrl) URL.revokeObjectURL(passportImageUrl);
      setPassportImage(file);
      setPassportImageUrl(file ? await createPreviewUrl(file) : null);
    }

    // Trigger immediate upload if file is selected (uses original File, not the converted blob)
    if (file) {
      uploadSingleFile(file, type);
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (driversLicenseFrontUrl) URL.revokeObjectURL(driversLicenseFrontUrl);
      if (driversLicenseBackUrl) URL.revokeObjectURL(driversLicenseBackUrl);
      if (passportImageUrl) URL.revokeObjectURL(passportImageUrl);
    };
  }, [driversLicenseFrontUrl, driversLicenseBackUrl, passportImageUrl]);

  // Validate file size (max 5 MB) — submit-time safety net; selection is already guarded.
  const validateFileSize = (file: File): boolean => {
    const maxSize = GOV_ID_MAX_FILE_SIZE_MB * 1024 * 1024;
    return file.size <= maxSize;
  };

  // Handle file preview (for non-image files or files without attachment_id)
  const handleFilePreview = async (
    file: { filepath?: string; name: string; attachment_id?: number },
    fileType: "ID_PROOF" | "ID_PROOF_BACK" = "ID_PROOF",
  ) => {
    if (!file.filepath) {
      return;
    }

    const applicantId = applicationData?.applicant_id;
    if (!applicantId) {
      return;
    }

    // If we have attachment_id, use secure preview with token
    if (file.attachment_id) {
      await ensureFileToken();
      // Collect all files with attachment_id from government_id arrays to create filesList
      const governmentIdFront = applicationData.government_id || [];
      const governmentIdBack = applicationData.government_id_back || [];

      // Combine all government ID files (front and back)
      const allGovernmentIdFiles = [
        ...(Array.isArray(governmentIdFront) ? governmentIdFront : []),
        ...(Array.isArray(governmentIdBack) ? governmentIdBack : []),
      ];

      // Filter files with attachment_id
      const allFilesWithIds = allGovernmentIdFiles.filter(
        (f): f is ApplicationFileAttachment & { attachment_id: number } =>
          f.attachment_id != null,
      );

      // Generate URLs for all files to create filesList for navigation (uses fresh token)
      const filesList = allFilesWithIds.map((f) => {
        const fileTypeForUrl = governmentIdFront.includes(f)
          ? "ID_PROOF"
          : "ID_PROOF_BACK";
        return generateFilePreviewUrl(
          f.attachment_id,
          fileTypeForUrl,
          applicantId,
          "APPLICATION",
        );
      });

      await openFilePreview(
        file.attachment_id,
        fileType,
        file.name || file.filepath,
        applicantId,
        "APPLICATION",
        filesList.length > 1 ? filesList : undefined, // Only pass if more than 1 file
      );
    } else {
      // Otherwise, use direct path preview (for stream-file URLs)
      let filePath = file.filepath.trim();

      // Check if filepath is already a full stream-file URL
      if (filePath.includes("/stream-file?path=")) {
        try {
          const url = new URL(filePath);
          const pathParam = url.searchParams.get("path");
          if (pathParam) {
            filePath = decodeURIComponent(pathParam);
          }
        } catch {
          // If URL parsing fails, try regex extraction
          const match = filePath.match(/path=([^&]+)/);
          if (match) {
            filePath = decodeURIComponent(match[1]);
          }
        }
      } else if (
        filePath.startsWith("http://") ||
        filePath.startsWith("https://")
      ) {
        // If it's a full URL but not stream-file, extract pathname
        try {
          const url = new URL(filePath);
          filePath = url.pathname.replace(/^\//, "");
        } catch {
          return;
        }
      }

      // Remove leading slash if present
      filePath = filePath.replace(/^\//, "");

      // Construct the preview URL
      const previewUrl = buildStreamFileUrl(filePath);

      // For stream-file URLs, collect all files from government_id arrays that don't have attachment_id
      const governmentIdFront = applicationData.government_id || [];
      const governmentIdBack = applicationData.government_id_back || [];
      const allGovernmentIdFiles = [
        ...(Array.isArray(governmentIdFront) ? governmentIdFront : []),
        ...(Array.isArray(governmentIdBack) ? governmentIdBack : []),
      ];

      const allFilesWithoutIds = allGovernmentIdFiles.filter(
        (f): f is ApplicationFileAttachment & { filepath: string } =>
          !f.attachment_id && !!f.filepath,
      );

      // Generate stream-file URLs for all files
      const filesList = allFilesWithoutIds
        .map((f) => {
          let fPath = f.filepath.trim();
          if (fPath.includes("/stream-file?path=")) {
            try {
              const url = new URL(fPath);
              const pathParam = url.searchParams.get("path");
              if (pathParam) {
                fPath = decodeURIComponent(pathParam);
              }
            } catch {
              const match = fPath.match(/path=([^&]+)/);
              if (match) {
                fPath = decodeURIComponent(match[1]);
              }
            }
          } else if (
            fPath.startsWith("http://") ||
            fPath.startsWith("https://")
          ) {
            try {
              const url = new URL(fPath);
              fPath = url.pathname.replace(/^\//, "");
            } catch {
              return null;
            }
          }
          fPath = fPath.replace(/^\//, "");
          return buildStreamFileUrl(fPath);
        })
        .filter((url): url is string => url !== null);

      // Open in file-handlers component with filename
      openFilePreviewByPath(
        previewUrl,
        file.name,
        filesList.length > 1 ? filesList : undefined,
      );
    }
  };

  // Handle government ID upload — kept for potential future use but currently not
  // called (upload is handled inline in handleSaveAndContinue). The _ prefix keeps
  // eslint quiet; @ts-expect-error covers tsc's noUnusedLocals (no _ exception there).
  // @ts-expect-error - intentionally unused, retained for future use
  const _handleUploadGovernmentId = async (): Promise<void> => {
    // Prevent API calls when application is read-only
    if (isReadOnly) {
      return;
    }

    if (!applicationData?.applicant_id) {
      setSaveMessage({
        type: "error",
        text: "Application ID not found. Please refresh the page.",
      });
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    // Validate files based on type
    if (governmentIdType === "drivers-license") {
      if (!driversLicenseFront) {
        setSaveMessage({
          type: "error",
          text: "Please upload Driver's License front image.",
        });
        setTimeout(() => setSaveMessage(null), 5000);
        return;
      }
      if (!driversLicenseBack) {
        setSaveMessage({
          type: "error",
          text: "Please upload Driver's License back image.",
        });
        setTimeout(() => setSaveMessage(null), 5000);
        return;
      }
      if (
        !validateFileSize(driversLicenseFront) ||
        !validateFileSize(driversLicenseBack)
      ) {
        setSaveMessage({ type: "error", text: APPLICANT_CONTENT.errors.signature.fileSize });
        setTimeout(() => setSaveMessage(null), 5000);
        return;
      }
    } else if (governmentIdType === "passport") {
      if (!passportImage) {
        setSaveMessage({
          type: "error",
          text: "Please upload Passport image.",
        });
        setTimeout(() => setSaveMessage(null), 5000);
        return;
      }
      if (!validateFileSize(passportImage)) {
        setSaveMessage({
          type: "error",
          text: APPLICANT_CONTENT.errors.signature.fileSizeSingle,
        });
        setTimeout(() => setSaveMessage(null), 5000);
        return;
      }
    }

    setUploadingGovId(true);
    setSaveMessage(null);

    try {
      const formData = new FormData();
      formData.append(
        "goverment_id_type",
        governmentIdType === "drivers-license" ? "Drivers License" : "Passport",
      );

      if (governmentIdType === "drivers-license") {
        if (driversLicenseFront) {
          formData.append("goverment_id_attachment", driversLicenseFront);
        }
        if (driversLicenseBack) {
          formData.append("goverment_id_attachment_back", driversLicenseBack);
        }
      } else {
        if (passportImage) {
          formData.append("goverment_id_attachment", passportImage);
        }
      }

      await apiClient.post(
        `/api/resident_application/${applicationData.applicant_id}`,
        formData,
      );

      // Fetch updated data
      await dispatch(
        fetchApplicationById(applicationData.applicant_id),
      ).unwrap();

      // Clear local file states after successful save so API files are displayed
      if (driversLicenseFront) {
        if (driversLicenseFrontUrl) {
          URL.revokeObjectURL(driversLicenseFrontUrl);
        }
        setDriversLicenseFront(null);
        setDriversLicenseFrontUrl(null);
        if (driversLicenseFrontRef.current) {
          driversLicenseFrontRef.current.value = "";
        }
      }
      if (driversLicenseBack) {
        if (driversLicenseBackUrl) {
          URL.revokeObjectURL(driversLicenseBackUrl);
        }
        setDriversLicenseBack(null);
        setDriversLicenseBackUrl(null);
        if (driversLicenseBackRef.current) {
          driversLicenseBackRef.current.value = "";
        }
      }
      if (passportImage) {
        if (passportImageUrl) {
          URL.revokeObjectURL(passportImageUrl);
        }
        setPassportImage(null);
        setPassportImageUrl(null);
        if (passportRef.current) {
          passportRef.current.value = "";
        }
      }

      setSaveMessage({
        type: "success",
        text: "Identity Verification uploaded successfully!",
      });
      setTimeout(() => setSaveMessage(null), 5000);
    } catch (error: unknown) {
      let errorMessage = "Failed to upload Identity Verification. Please try again.";
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      }
      setSaveMessage({ type: "error", text: errorMessage });
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      setUploadingGovId(false);
    }
  };

  // Handle save and continue - Validate ALL fields before making ANY API calls (exitAfterSave = true for Save & Exit)
  const handleSaveAndContinue = async (exitAfterSave = false) => {
    const navigateToDashboardAfterSave = exitAfterSave;
    // If read-only mode, just navigate to next section without saving
    if (isReadOnly) {
      const paymentPath = applicantId
        ? getTabPathWithApplicantId("payment", applicantId)
        : "/applicant/application/payment";
      if (applicantId) {
        navigate(paymentPath, getTabNavigationOptions("payment"));
      } else {
        navigate(paymentPath);
      }
      return;
    }

    // Clear any previous messages
    setSaveMessage(null);

    // Validation 1: Application ID check (no API call needed)
    if (!applicationData?.applicant_id) {
      setSaveMessage({
        type: "error",
        text: "Application ID not found. Please refresh the page.",
      });
      setTimeout(() => setSaveMessage(null), 5000);
      return; // Stop - no API calls
    }

    // Validate all fields
    const errors: typeof fieldErrors = {};

    // Validation 2: All terms must be checked (no API call needed)
    if (!allDocumentsChecked) {
      errors.allDocumentsChecked = APPLICANT_CONTENT.errors.signature.allDocumentsChecked;
    }

    // Validation 3: Government ID type selected (no API call needed)
    if (!governmentIdType) {
      errors.governmentIdType = APPLICANT_CONTENT.errors.signature.governmentIdType;
    }

    // Validation 4: Government ID files uploaded (no API call needed)
    // Check if government ID already exists - handle both object and array formats
    const hasGovIdAttachment =
      normalizeGovIdAttachments(applicationData?.goverment_id_attachment).length >
      0;

    // Check for back attachment (drivers license only)
    const hasGovIdAttachmentBack =
      governmentIdType === "passport" ||
      normalizeGovIdAttachments(applicationData?.goverment_id_attachment_back)
        .length > 0;

    // Check if files need to be uploaded
    const needsGovIdUpload =
      !hasGovIdAttachment ||
      (governmentIdType === "drivers-license" && !hasGovIdAttachmentBack);

    if (needsGovIdUpload) {
      if (governmentIdType === "drivers-license") {
        if (!driversLicenseFront || !driversLicenseBack) {
          errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.governmentIdFiles;
        } else {
          // Validate file sizes
          if (driversLicenseFront && !validateFileSize(driversLicenseFront)) {
            errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.driversLicenseFrontSize;
          } else if (
            driversLicenseBack &&
            !validateFileSize(driversLicenseBack)
          ) {
            errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.driversLicenseBackSize;
          }
        }
      } else if (governmentIdType === "passport") {
        if (!passportImage) {
          errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.governmentIdFilesPassport;
        } else if (passportImage && !validateFileSize(passportImage)) {
          errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.passportImageSize;
        }
      }
    }

    // Validation 5: Signature required (no API call needed)
    if (!signature.trim()) {
      errors.signature = APPLICANT_CONTENT.errors.signature.signature;
    } else if (!validateSignature(signature)) {
      setValidationModalMessage(APPLICANT_CONTENT.errors.signature.signatureMismatch);
      setValidationShowIcon(false);
      setShowValidationModal(true);
      const firstName = applicationData?.first_name || "";
      const lastName = applicationData?.last_name || "";
      errors.signature = `Signature must match "${firstName} ${lastName}"`;
    }

    // Validation 7: Access statement required (no API call needed)
    if (!accessStatementChecked) {
      errors.accessStatement = "'Statements' is required.";
    }

    // Validation 8: SSN validation (no API call needed)
    if (!ssnDigits || ssnDigits.length !== 9) {
      errors.ssn = APPLICANT_CONTENT.errors.signature.ssnDigits;
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setValidateForm(true);
      // Show validation popup with all errors
      const errorMessages: string[] = [];
      if (errors.allDocumentsChecked)
        errorMessages.push(
          `Terms and Documents: ${errors.allDocumentsChecked}`,
        );
      if (errors.governmentIdType)
        errorMessages.push(`Identity Verification Type: ${errors.governmentIdType}`);
      if (errors.governmentIdFiles)
        errorMessages.push(`Identity Verification Files: ${errors.governmentIdFiles}`);
      if (errors.signature)
        errorMessages.push(`Signature: ${errors.signature}`);
      if (errors.accessStatement)
        errorMessages.push(`Agreement: ${errors.accessStatement}`);
      if (errors.ssn) errorMessages.push(`SSN: ${errors.ssn}`);
      if (errorMessages.length > 0) {
        setValidationModalMessage(errorMessages.join("\n"));
        setValidationShowIcon(true);
        setShowValidationModal(true);
      }
      return;
    }

    // Clear errors if validation passes
    setFieldErrors({});
    setValidateForm(false);
    setSignatureError("");

    // ALL VALIDATIONS PASSED - Now proceed with API calls

    if (navigateToDashboardAfterSave) {
      setIsSavingExit(true);
    } else {
      setIsSaving(true);
    }
    setSaveMessage(null);

    try {
      // Prepare signature data as JSON string (text signature mode) - matching API format
      const signatureData = {
        signature: signature.trim(),
        signaturetype: "type1", // Default signature type
      };

      // Single source of truth for the non-file signature payload (also used by auto-save).
      const updateData = buildSignatureUpdateData();

      // If government ID files are being uploaded now, upload them with signature data together
      if (needsGovIdUpload) {
        // Upload government ID files along with signature data in one FormData call
        const formData = new FormData();

        // Add government ID type and files
        if (
          governmentIdType === "drivers-license" &&
          driversLicenseFront &&
          driversLicenseBack
        ) {
          formData.append("goverment_id_type", "Drivers License");
          formData.append("goverment_id_attachment", driversLicenseFront);
          formData.append("goverment_id_attachment_back", driversLicenseBack);
        } else if (governmentIdType === "passport" && passportImage) {
          formData.append("goverment_id_type", "Passport");
          formData.append("goverment_id_attachment", passportImage);
        }

        // Add signature data fields (matching API format)
        formData.append("digital_signature", JSON.stringify(signatureData));
        formData.append("access_statement", accessStatementChecked ? "1" : "0");
        formData.append("uc_optin", ucOptIn ? "1" : "0");
        formData.append("all_links_read", allDocumentsChecked ? "1" : "0");
        formData.append("enter_date", JSON.stringify({}));
        formData.append("validation_scope", "tab");
        formData.append("validation_tab", "signature");
        formData.append("validation", "1");

        // Upload everything together in one API call
        const response = await apiClient.post(
          `/api/resident_application/${applicationData.applicant_id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        // Check for backend error even if status is 200
        const responseData = response.data;
        if (
          responseData &&
          (responseData.success === false || responseData.error)
        ) {
          throw new Error(
            responseData.message ||
            responseData.error ||
            "Failed to save signature.",
          );
        }
      } else {
        // Government ID already exists, just save signature data using updateApplication
        // Match the format from other forms (like History page) - using updateApplication
        await dispatch(
          updateApplication({
            applicantId: applicationData.applicant_id,
            updateData: updateData,
          }),
        ).unwrap();
      }

      // Fetch updated data
      await dispatch(
        fetchApplicationById(applicationData.applicant_id),
      ).unwrap();

      // Clear local file states after successful save so API files are displayed
      if (driversLicenseFront) {
        if (driversLicenseFrontUrl) {
          URL.revokeObjectURL(driversLicenseFrontUrl);
        }
        setDriversLicenseFront(null);
        setDriversLicenseFrontUrl(null);
        if (driversLicenseFrontRef.current) {
          driversLicenseFrontRef.current.value = "";
        }
      }
      if (driversLicenseBack) {
        if (driversLicenseBackUrl) {
          URL.revokeObjectURL(driversLicenseBackUrl);
        }
        setDriversLicenseBack(null);
        setDriversLicenseBackUrl(null);
        if (driversLicenseBackRef.current) {
          driversLicenseBackRef.current.value = "";
        }
      }
      if (passportImage) {
        if (passportImageUrl) {
          URL.revokeObjectURL(passportImageUrl);
        }
        setPassportImage(null);
        setPassportImageUrl(null);
        if (passportRef.current) {
          passportRef.current.value = "";
        }
      }

      setSaveMessage({
        type: "success",
        text: "Signature saved successfully!",
      });
      sendAnalytics('autosave', 'resident application info', applicationData?.applicant_id);

      // Navigate to dashboard (Save & Exit) or payment page (Save & Continue)
      setTimeout(() => {
        if (navigateToDashboardAfterSave) {
          navigate("/applicant/dashboard", { replace: true });
        } else {
          const paymentPath = applicantId
            ? getTabPathWithApplicantId("payment", applicantId)
            : "/applicant/application/payment";
          if (applicantId) {
            navigate(paymentPath, {
              ...getTabNavigationOptions("payment"),
              replace: true,
            });
          } else {
            navigate(paymentPath, { replace: true });
          }
        }
      }, 300);
    } catch (error) {
      sendAnalytics('autosave', 'resident application info error', applicationData?.applicant_id);
      // Clear Redux error state immediately to prevent parent ResidentApplication
      // component's useEffect from redirecting to /applicant/dashboard.
      // The parent watches selectApplicationError and navigates away on any error,
      // but here we want to handle the error locally (show popup/message).
      dispatch(clearError());

      // Check if error is related to signature name mismatch
      let errorMessage =
        typeof error === "string"
          ? error
          : "Failed to save signature. Please try again.";

      // Try to extract error message from API response (prefer server body over axios generic message)
      if (error && typeof error === "object") {
        const apiError = error as {
          response?: {
            data?:
              | string
              | { message?: string; error?: string | { message?: string } };
          };
          message?: string;
          error?: string | { message?: string };
        };

        if (apiError.response?.data) {
          const responseData = apiError.response.data;
          if (typeof responseData === "string") {
            errorMessage = responseData;
          } else if (typeof responseData === "object" && responseData.message) {
            errorMessage = responseData.message;
          } else if (typeof responseData === "object" && responseData.error) {
            errorMessage =
              typeof responseData.error === "string"
                ? responseData.error
                : responseData.error?.message ?? errorMessage;
          }
        } else if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.error) {
          if (typeof apiError.error === "string") {
            errorMessage = apiError.error;
          } else if (
            typeof apiError.error === "object" &&
            apiError.error.message
          ) {
            errorMessage = apiError.error.message;
          }
        }
      }

      // Check if error message contains signature validation error
      const errorString = String(errorMessage);
      if (
        errorString.includes(
          APPLICANT_CONTENT.errors.signature.signatureMismatch.slice(0, 57),
        )
      ) {
        // Handle signature name mismatch - show popup and log the event
        await handleSignatureNameMismatch();
        setIsSaving(false);
        setIsSavingExit(false);
        return; // Don't show error in saveMessage, popup is already shown
      }

      // Same UX as client-side tab validation: modal with server message (e.g. SSN / ITIN required)
      setValidationModalMessage(errorMessage);
      setValidationShowIcon(true);
      setShowValidationModal(true);
    } finally {
      setIsSaving(false);
      setIsSavingExit(false);
    }
  };

  // Build the signature tab's non-file API payload from current form state.
  // Single source of truth for both the Save button and auto-save-on-tab-change.
  // (Government ID file uploads are handled separately via FormData and are NOT
  // part of this payload — the auto-saver does the plain updateApplication PUT only.)
  const buildSignatureUpdateData = (): UpdateApplicationRequest => {
    const signatureData = {
      signature: signature.trim(),
      signaturetype: "type1",
    };

    const updateData: UpdateApplicationRequest = {
      ...residentTabValidation("signature"),
      digital_signature: JSON.stringify(signatureData), // JSON string format
      access_statement: accessStatementChecked ? "1" : "0", // From checkbox
      uc_optin: ucOptIn ? "1" : "0", // Utility Concierge opt-in from checkbox
      all_links_read: allDocumentsChecked ? "1" : "0", // String format
      enter_date: {}, // Required empty object (like History page)
    };

    // Remove undefined fields (like About page does)
    Object.keys(updateData).forEach((key) => {
      if (updateData[key as keyof typeof updateData] === undefined) {
        delete updateData[key as keyof typeof updateData];
      }
    });

    return updateData;
  };

  // Auto-save-on-tab-change (Angular submitForm('fromtab') parity): persist the
  // current tab WITHOUT navigating or showing dialogs. Government ID file uploads
  // are excluded — this is the plain updateApplication PUT only. Dirty-guarded by
  // useTabAutoSave below.
  const autoSaveSignature = async (): Promise<void> => {
    if (!applicationData?.applicant_id) return;
    await dispatch(
      updateApplication({
        applicantId: applicationData.applicant_id,
        updateData: buildSignatureUpdateData(),
      }),
    ).unwrap();
    await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap();
  };

  useTabAutoSave({
    name: "signature",
    snapshot: buildSignatureUpdateData(),
    loadedKey: applicationData?.applicant_id,
    save: autoSaveSignature,
    isReadOnly,
  });

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(
          applicationData,
          APPLICANT_CONTENT.application.status.incomplete,
        )}
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div data-testid="signature-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <PenTool className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {APPLICANT_CONTENT.application.steps.signature.name}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {APPLICANT_CONTENT.application.steps.signature.subtitle}
              </p>
            </div>
          </div>


          {/* Disclosures, Certifications, SSN & Signature */}
          <div className="mb-8 p-6 rounded-lg border-2 border-gray-300">

            {/* Identity Verification Section - header inside box above preview text */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-base font-bold text-gray-700 mb-3 flex items-center gap-2 relative">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <IdCard className="w-4 h-4 text-violet-600" />
                </span>
                Identity Verification <span className="text-red-500">*</span>
                <div className="group">
                  <Info className="w-5 h-5 text-green-600 cursor-help" />
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-full sm:w-96 max-w-[calc(100vw-3rem)] p-4 bg-amber-50 border-2 border-amber-200 rounded-lg shadow-xl z-10">
                    <p className="text-xs text-gray-500 mb-3">
                      Please preview uploaded files. Unreadable files will delay
                      your application.
                    </p>
                    <p className="text-xs text-gray-700 mt-2">
                      We cannot accept links/shared files, please download files to your computer or phone directly and upload to our site.
                    </p>
                  </div>
                </div>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Please preview uploaded files. Unreadable files will delay your
                application.
              </p>
              <div className="flex gap-4 mb-4">
                <label
                  className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : ""}`}
                >
                  <input
                    data-testid="signature-gov-id-drivers-license-radio"
                    type="radio"
                    name="governmentId"
                    value="drivers-license"
                    checked={governmentIdType === "drivers-license"}
                    onChange={() => {
                      if (!isReadOnly) {
                        setGovernmentIdType("drivers-license");
                        setPassportImage(null);
                        if (fieldErrors.governmentIdType) {
                          setFieldErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.governmentIdType;
                            return newErrors;
                          });
                        }
                      }
                    }}
                    disabled={isReadOnly}
                    className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                  />
                  <span className="text-sm text-gray-700">Drivers License</span>
                </label>
                <label
                  className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : ""}`}
                >
                  <input
                    data-testid="signature-gov-id-passport-radio"
                    type="radio"
                    name="governmentId"
                    value="passport"
                    checked={governmentIdType === "passport"}
                    onChange={() => {
                      if (!isReadOnly) {
                        setGovernmentIdType("passport");
                        setDriversLicenseFront(null);
                        setDriversLicenseBack(null);
                        if (fieldErrors.governmentIdType) {
                          setFieldErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.governmentIdType;
                            return newErrors;
                          });
                        }
                      }
                    }}
                    disabled={isReadOnly}
                    className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                  />
                  <span className="text-sm text-gray-700">Passport</span>
                </label>
              </div>
              {validateForm && fieldErrors.governmentIdType && (
                <div className="mb-4 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.governmentIdType}
                </div>
              )}
              {validateForm && fieldErrors.governmentIdFiles && (
                <div className="mb-4 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.governmentIdFiles}
                </div>
              )}

              {/* Driver's License Upload Fields */}
              {governmentIdType === "drivers-license" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-lg bg-white">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload image or PDF of Driver's License (Front){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Image or PDF, max 5 MB</p>
                    <div className="flex items-center gap-3">
                      <input
                        ref={driversLicenseFrontRef}
                        type="file"
                        accept={GOV_ID_FILE_ACCEPT}
                        onChange={(e) => {
                          if (!isReadOnly) {
                            const file = e.target.files?.[0] || null;
                            handleFileUpload(file, "front");
                          }
                        }}
                        disabled={isReadOnly}
                        className="hidden"
                        id="drivers-license-front"
                      />
                      <label
                        htmlFor="drivers-license-front"
                        className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors inline-flex items-center gap-2 ${isReadOnly
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                          }`}
                        onClick={(e) => {
                          if (isReadOnly) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingFront ? "Uploading..." : "Upload"}
                        {uploadingFront && (
                          <Loader2 className="w-4 h-4 animate-spin ml-1" />
                        )}
                      </label>
                    </div>
                    {/* Show file for newly uploaded file */}
                    {driversLicenseFrontUrl && driversLicenseFront && (
                      <div className="mt-3">
                        {(() => {
                          const fileName = driversLicenseFront.name;
                          const isImage = isImageFile(fileName);

                          // Collect all files (newly uploaded + existing) for filesList
                          const allFiles: string[] = [];

                          // Add newly uploaded files (blob URLs)
                          if (driversLicenseFrontUrl)
                            allFiles.push(driversLicenseFrontUrl);
                          if (driversLicenseBackUrl)
                            allFiles.push(driversLicenseBackUrl);
                          if (passportImageUrl) allFiles.push(passportImageUrl);

                          // Add existing files from API
                          const governmentIdFront =
                            applicationData?.government_id || [];
                          const governmentIdBack =
                            applicationData?.government_id_back || [];
                          const allGovernmentIdFiles = [
                            ...(Array.isArray(governmentIdFront)
                              ? governmentIdFront
                              : []),
                            ...(Array.isArray(governmentIdBack)
                              ? governmentIdBack
                              : []),
                          ];
                          const allFilesWithIds = allGovernmentIdFiles.filter(
                            (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                          );
                          const applicantId = applicationData?.applicant_id;
                          if (applicantId) {
                            allFilesWithIds.forEach((f) => {
                              const fileTypeForUrl = governmentIdFront.includes(f)
                                ? "ID_PROOF"
                                : "ID_PROOF_BACK";
                              allFiles.push(
                                generateFilePreviewUrl(
                                  f.attachment_id,
                                  fileTypeForUrl,
                                  applicantId,
                                  "APPLICATION",
                                ),
                              );
                            });
                          }

                          const filesList =
                            allFiles.length > 1 ? allFiles : undefined;

                          const openPreviewWithFreshToken = async () => {
                            await ensureFileToken();
                            const freshAllFiles: string[] = [];
                            if (driversLicenseFrontUrl)
                              freshAllFiles.push(driversLicenseFrontUrl);
                            if (driversLicenseBackUrl)
                              freshAllFiles.push(driversLicenseBackUrl);
                            if (passportImageUrl)
                              freshAllFiles.push(passportImageUrl);
                            const govFront = applicationData?.government_id || [];
                            const govBack =
                              applicationData?.government_id_back || [];
                            const allGovFiles = [
                              ...(Array.isArray(govFront) ? govFront : []),
                              ...(Array.isArray(govBack) ? govBack : []),
                            ];
                            const withIds = allGovFiles.filter(
                              (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                            );
                            const applicantId = applicationData?.applicant_id;
                            if (applicantId) {
                              withIds.forEach((f) => {
                                const ft = govFront.includes(f)
                                  ? "ID_PROOF"
                                  : "ID_PROOF_BACK";
                                freshAllFiles.push(
                                  generateFilePreviewUrl(
                                    f.attachment_id,
                                    ft,
                                    applicantId,
                                    "APPLICATION",
                                  ),
                                );
                              });
                            }
                            const list =
                              freshAllFiles.length > 1
                                ? freshAllFiles
                                : undefined;
                            openFilePreviewByPath(
                              driversLicenseFrontUrl || "",
                              fileName || "driver-license-front.pdf",
                              list,
                            );
                          };

                          const isPdf = isDocumentFile(fileName);

                          return (
                            <div className="mb-2">
                              <div
                                onClick={() => void openPreviewWithFreshToken()}
                                className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                              >
                                {isPdf && (
                                  <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                                )}
                                <span className="text-sm text-gray-700 flex-1 truncate">
                                  {fileName}
                                </span>
                              </div>
                              {isImage && (
                                <div className="mt-2">
                                  <Thumbnail
                                    filepath={driversLicenseFrontUrl || ""}
                                    fileName={fileName}
                                    filesList={filesList}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {/* Show files for existing files from API */}
                    {!driversLicenseFront &&
                      applicationData &&
                      (() => {
                        const govIdAttachment =
                          applicationData.goverment_id_attachment;
                        if (!govIdAttachment) return null;

                        const attachments =
                          normalizeGovIdAttachments(govIdAttachment);

                        if (attachments.length === 0) return null;

                        return (
                          <div className="mt-3 space-y-2">
                            {attachments.map((file: ApplicationFileAttachment, index: number) => {
                              const fileName =
                                file.filename ||
                                file.name ||
                                "driver-license-front";
                              const isImage =
                                file.filepath && isImageFile(fileName);

                              return (
                                <div
                                  key={file.attachment_id || index}
                                  className="mb-2"
                                >
                                  <div
                                    onClick={() =>
                                      handleFilePreview(
                                        {
                                          filepath: file.filepath,
                                          name: fileName,
                                          attachment_id: file.attachment_id,
                                        },
                                        "ID_PROOF",
                                      )
                                    }
                                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                  >
                                    <span className="text-sm text-gray-700 flex-1 truncate">
                                      {fileName}
                                    </span>
                                  </div>
                                  {isImage &&
                                    (() => {
                                      // Collect all files for filesList
                                      const governmentIdFront =
                                        applicationData?.government_id || [];
                                      const governmentIdBack =
                                        applicationData?.government_id_back || [];
                                      const allGovernmentIdFiles = [
                                        ...(Array.isArray(governmentIdFront)
                                          ? governmentIdFront
                                          : []),
                                        ...(Array.isArray(governmentIdBack)
                                          ? governmentIdBack
                                          : []),
                                      ];
                                      const allFilesWithIds =
                                        allGovernmentIdFiles.filter(
                                          (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                                        );
                                      const filesList =
                                        allFilesWithIds.length > 1
                                          ? allFilesWithIds.map((f) => {
                                            const fileTypeForUrl =
                                              governmentIdFront.includes(f)
                                                ? "ID_PROOF"
                                                : "ID_PROOF_BACK";
                                            return generateFilePreviewUrl(
                                              f.attachment_id,
                                              fileTypeForUrl,
                                              applicationData?.applicant_id ||
                                              0,
                                              "APPLICATION",
                                            );
                                          })
                                          : undefined;

                                      return (
                                        <div className="mt-2">
                                          <Thumbnail
                                            filepath={file.filepath || ""}
                                            attachmentId={file.attachment_id}
                                            fileType="ID_PROOF"
                                            fileName={fileName}
                                            refId={applicationData?.applicant_id}
                                            module="APPLICATION"
                                            filesList={filesList}
                                          />
                                        </div>
                                      );
                                    })()}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg bg-white">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload image or PDF of Driver's License (Back){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Image or PDF, max 5 MB</p>
                    <div className="flex items-center gap-3">
                      <input
                        ref={driversLicenseBackRef}
                        type="file"
                        accept={GOV_ID_FILE_ACCEPT}
                        onChange={(e) => {
                          if (!isReadOnly) {
                            const file = e.target.files?.[0] || null;
                            handleFileUpload(file, "back");
                          }
                        }}
                        disabled={isReadOnly}
                        className="hidden"
                        id="drivers-license-back"
                      />
                      <label
                        htmlFor="drivers-license-back"
                        className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors inline-flex items-center gap-2 ${isReadOnly
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                          }`}
                        onClick={(e) => {
                          if (isReadOnly) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingBack ? "Uploading..." : "Upload"}
                        {uploadingBack && (
                          <Loader2 className="w-4 h-4 animate-spin ml-1" />
                        )}
                      </label>
                    </div>
                    {/* Show file for newly uploaded file */}
                    {driversLicenseBackUrl && driversLicenseBack && (
                      <div className="mt-3">
                        {(() => {
                          const fileName = driversLicenseBack.name;
                          const isImage = isImageFile(fileName);

                          // Collect all files (newly uploaded + existing) for filesList
                          const allFiles: string[] = [];

                          // Add newly uploaded files (blob URLs)
                          if (driversLicenseFrontUrl)
                            allFiles.push(driversLicenseFrontUrl);
                          if (driversLicenseBackUrl)
                            allFiles.push(driversLicenseBackUrl);
                          if (passportImageUrl) allFiles.push(passportImageUrl);

                          // Add existing files from API
                          const governmentIdFront =
                            applicationData?.government_id || [];
                          const governmentIdBack =
                            applicationData?.government_id_back || [];
                          const allGovernmentIdFiles = [
                            ...(Array.isArray(governmentIdFront)
                              ? governmentIdFront
                              : []),
                            ...(Array.isArray(governmentIdBack)
                              ? governmentIdBack
                              : []),
                          ];
                          const allFilesWithIds = allGovernmentIdFiles.filter(
                            (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                          );
                          const applicantId = applicationData?.applicant_id;
                          if (applicantId) {
                            allFilesWithIds.forEach((f) => {
                              const fileTypeForUrl = governmentIdFront.includes(f)
                                ? "ID_PROOF"
                                : "ID_PROOF_BACK";
                              allFiles.push(
                                generateFilePreviewUrl(
                                  f.attachment_id,
                                  fileTypeForUrl,
                                  applicantId,
                                  "APPLICATION",
                                ),
                              );
                            });
                          }

                          const filesList =
                            allFiles.length > 1 ? allFiles : undefined;

                          const openBackPreviewWithFreshToken = async () => {
                            await ensureFileToken();
                            const freshAllFiles: string[] = [];
                            if (driversLicenseFrontUrl)
                              freshAllFiles.push(driversLicenseFrontUrl);
                            if (driversLicenseBackUrl)
                              freshAllFiles.push(driversLicenseBackUrl);
                            if (passportImageUrl)
                              freshAllFiles.push(passportImageUrl);
                            const govFront = applicationData?.government_id || [];
                            const govBack =
                              applicationData?.government_id_back || [];
                            const allGovFiles = [
                              ...(Array.isArray(govFront) ? govFront : []),
                              ...(Array.isArray(govBack) ? govBack : []),
                            ];
                            const withIds = allGovFiles.filter(
                              (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                            );
                            const applicantId = applicationData?.applicant_id;
                            if (applicantId) {
                              withIds.forEach((f) => {
                                const ft = govFront.includes(f)
                                  ? "ID_PROOF"
                                  : "ID_PROOF_BACK";
                                freshAllFiles.push(
                                  generateFilePreviewUrl(
                                    f.attachment_id,
                                    ft,
                                    applicantId,
                                    "APPLICATION",
                                  ),
                                );
                              });
                            }
                            const list =
                              freshAllFiles.length > 1
                                ? freshAllFiles
                                : undefined;
                            openFilePreviewByPath(
                              driversLicenseBackUrl || "",
                              fileName || "driver-license-back.pdf",
                              list,
                            );
                          };

                          const isPdf = isDocumentFile(fileName);

                          return (
                            <div className="mb-2">
                              <div
                                onClick={() =>
                                  void openBackPreviewWithFreshToken()
                                }
                                className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                              >
                                {isPdf && (
                                  <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                                )}
                                <span className="text-sm text-gray-700 flex-1 truncate">
                                  {fileName}
                                </span>
                              </div>
                              {isImage && (
                                <div className="mt-2">
                                  <Thumbnail
                                    filepath={driversLicenseBackUrl || ""}
                                    fileName={fileName}
                                    filesList={filesList}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {/* Show files for existing files from API */}
                    {!driversLicenseBack &&
                      applicationData &&
                      (() => {
                        const govIdAttachmentBack =
                          applicationData.goverment_id_attachment_back;
                        if (!govIdAttachmentBack) return null;

                        const backAttachments =
                          normalizeGovIdAttachments(govIdAttachmentBack);

                        if (backAttachments.length === 0) return null;

                        return (
                          <div className="mt-3 space-y-2">
                            {backAttachments.map((file: ApplicationFileAttachment, index: number) => {
                              const fileName =
                                file.filename ||
                                file.name ||
                                "driver-license-back";
                              const isImage =
                                file.filepath && isImageFile(fileName);

                              return (
                                <div
                                  key={file.attachment_id || index}
                                  className="mb-2"
                                >
                                  <div
                                    onClick={() =>
                                      handleFilePreview(
                                        {
                                          filepath: file.filepath,
                                          name: fileName,
                                          attachment_id: file.attachment_id,
                                        },
                                        "ID_PROOF_BACK",
                                      )
                                    }
                                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                  >
                                    <span className="text-sm text-gray-700 flex-1 truncate">
                                      {fileName}
                                    </span>
                                  </div>
                                  {isImage &&
                                    (() => {
                                      // Collect all files for filesList
                                      const governmentIdFront =
                                        applicationData?.government_id || [];
                                      const governmentIdBack =
                                        applicationData?.government_id_back || [];
                                      const allGovernmentIdFiles = [
                                        ...(Array.isArray(governmentIdFront)
                                          ? governmentIdFront
                                          : []),
                                        ...(Array.isArray(governmentIdBack)
                                          ? governmentIdBack
                                          : []),
                                      ];
                                      const allFilesWithIds =
                                        allGovernmentIdFiles.filter(
                                          (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                                        );
                                      const filesList =
                                        allFilesWithIds.length > 1
                                          ? allFilesWithIds.map((f) => {
                                            const fileTypeForUrl =
                                              governmentIdFront.includes(f)
                                                ? "ID_PROOF"
                                                : "ID_PROOF_BACK";
                                            return generateFilePreviewUrl(
                                              f.attachment_id,
                                              fileTypeForUrl,
                                              applicationData?.applicant_id ||
                                              0,
                                              "APPLICATION",
                                            );
                                          })
                                          : undefined;

                                      return (
                                        <div className="mt-2">
                                          <Thumbnail
                                            filepath={file.filepath || ""}
                                            attachmentId={file.attachment_id}
                                            fileType="ID_PROOF_BACK"
                                            fileName={fileName}
                                            refId={applicationData?.applicant_id}
                                            module="APPLICATION"
                                            filesList={filesList}
                                          />
                                        </div>
                                      );
                                    })()}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                  </div>
                </div>
              )}

              {/* Passport Upload Field */}
              {governmentIdType === "passport" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload image or PDF of Passport{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Image or PDF, max 5 MB</p>
                  <div className="flex items-center gap-3">
                    <input
                      ref={passportRef}
                      type="file"
                      accept={GOV_ID_FILE_ACCEPT}
                      onChange={(e) => {
                        if (!isReadOnly) {
                          const file = e.target.files?.[0] || null;
                          handleFileUpload(file, "passport");
                        }
                      }}
                      disabled={isReadOnly}
                      className="hidden"
                      id="passport"
                    />
                    <label
                      htmlFor="passport"
                      className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors inline-flex items-center gap-2 ${isReadOnly
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                        }`}
                      onClick={(e) => {
                        if (isReadOnly) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingPassport ? "Uploading..." : "Upload"}
                      {uploadingPassport && (
                        <Loader2 className="w-4 h-4 animate-spin ml-1" />
                      )}
                    </label>
                  </div>
                  {/* Show file for newly uploaded file */}
                  {passportImageUrl && passportImage && (
                    <div className="mt-3">
                      {(() => {
                        const fileName = passportImage.name;
                        const isImage = isImageFile(fileName);

                        // Collect all files (newly uploaded + existing) for filesList
                        const allFiles: string[] = [];

                        // Add newly uploaded files (blob URLs)
                        if (driversLicenseFrontUrl)
                          allFiles.push(driversLicenseFrontUrl);
                        if (driversLicenseBackUrl)
                          allFiles.push(driversLicenseBackUrl);
                        if (passportImageUrl) allFiles.push(passportImageUrl);

                        // Add existing files from API
                        const governmentIdFront =
                          applicationData?.government_id || [];
                        const governmentIdBack =
                          applicationData?.government_id_back || [];
                        const allGovernmentIdFiles = [
                          ...(Array.isArray(governmentIdFront)
                            ? governmentIdFront
                            : []),
                          ...(Array.isArray(governmentIdBack)
                            ? governmentIdBack
                            : []),
                        ];
                        const allFilesWithIds = allGovernmentIdFiles.filter(
                              (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                                f.attachment_id != null,
                        );
                        const applicantId = applicationData?.applicant_id;
                        if (applicantId) {
                          allFilesWithIds.forEach((f) => {
                            const fileTypeForUrl = governmentIdFront.includes(f)
                              ? "ID_PROOF"
                              : "ID_PROOF_BACK";
                            allFiles.push(
                              generateFilePreviewUrl(
                                f.attachment_id,
                                fileTypeForUrl,
                                applicantId,
                                "APPLICATION",
                              ),
                            );
                          });
                        }

                        const filesList =
                          allFiles.length > 1 ? allFiles : undefined;

                        const openPassportPreviewWithFreshToken = async () => {
                          await ensureFileToken();
                          const freshAllFiles: string[] = [];
                          if (driversLicenseFrontUrl)
                            freshAllFiles.push(driversLicenseFrontUrl);
                          if (driversLicenseBackUrl)
                            freshAllFiles.push(driversLicenseBackUrl);
                          if (passportImageUrl)
                            freshAllFiles.push(passportImageUrl);
                          const govFront = applicationData?.government_id || [];
                          const govBack =
                            applicationData?.government_id_back || [];
                          const allGovFiles = [
                            ...(Array.isArray(govFront) ? govFront : []),
                            ...(Array.isArray(govBack) ? govBack : []),
                          ];
                          const withIds = allGovFiles.filter(
                            (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                          );
                          const applicantId = applicationData?.applicant_id;
                          if (applicantId) {
                            withIds.forEach((f) => {
                              const ft = govFront.includes(f)
                                ? "ID_PROOF"
                                : "ID_PROOF_BACK";
                              freshAllFiles.push(
                                generateFilePreviewUrl(
                                  f.attachment_id,
                                  ft,
                                  applicantId,
                                  "APPLICATION",
                                ),
                              );
                            });
                          }
                          const list =
                            freshAllFiles.length > 1 ? freshAllFiles : undefined;
                          openFilePreviewByPath(
                            passportImageUrl || "",
                            fileName || "passport.pdf",
                            list,
                          );
                        };

                        const isPdf = isDocumentFile(fileName);

                        return (
                          <div className="mb-2">
                            <div
                              onClick={() =>
                                void openPassportPreviewWithFreshToken()
                              }
                              className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                            >
                              {isPdf && (
                                <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                              )}
                              <span className="text-sm text-gray-700 flex-1 truncate">
                                {fileName}
                              </span>
                            </div>
                            {isImage && (
                              <div className="mt-2">
                                <Thumbnail
                                  filepath={passportImageUrl || ""}
                                  fileName={fileName}
                                  filesList={filesList}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* Show files for existing files from API */}
                  {!passportImage &&
                    applicationData &&
                    (() => {
                      const govIdAttachment =
                        applicationData.goverment_id_attachment;
                      if (!govIdAttachment) return null;

                      const attachments =
                        normalizeGovIdAttachments(govIdAttachment);

                      if (attachments.length === 0) return null;

                      return (
                        <div className="mt-3 space-y-2">
                          {attachments.map((file: ApplicationFileAttachment, index: number) => {
                            const fileName =
                              file.filename || file.name || "passport";
                            const isImage =
                              file.filepath && isImageFile(fileName);

                            return (
                              <div
                                key={file.attachment_id || index}
                                className="mb-2"
                              >
                                <div
                                  onClick={() =>
                                    handleFilePreview(
                                      {
                                        filepath: file.filepath,
                                        name: fileName,
                                        attachment_id: file.attachment_id,
                                      },
                                      "ID_PROOF",
                                    )
                                  }
                                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                >
                                  <span className="text-sm text-gray-700 flex-1 truncate">
                                    {fileName}
                                  </span>
                                </div>
                                {isImage &&
                                  (() => {
                                    // Collect all files for filesList (passport uses ID_PROOF type)
                                    const governmentIdFront =
                                      applicationData.government_id || [];
                                    const governmentIdBack =
                                      applicationData.government_id_back || [];
                                    const allGovernmentIdFiles = [
                                      ...(Array.isArray(governmentIdFront)
                                        ? governmentIdFront
                                        : []),
                                      ...(Array.isArray(governmentIdBack)
                                        ? governmentIdBack
                                        : []),
                                    ];
                                    const allFilesWithIds =
                                      allGovernmentIdFiles.filter(
                                        (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                              f.attachment_id != null,
                                      );
                                    const filesList =
                                      allFilesWithIds.length > 1
                                        ? allFilesWithIds.map((f) => {
                                          const fileTypeForUrl =
                                            governmentIdFront.includes(f)
                                              ? "ID_PROOF"
                                              : "ID_PROOF_BACK";
                                          return generateFilePreviewUrl(
                                            f.attachment_id,
                                            fileTypeForUrl,
                                            applicationData?.applicant_id || 0,
                                            "APPLICATION",
                                          );
                                        })
                                        : undefined;

                                    return (
                                      <div className="mt-2">
                                        <Thumbnail
                                          filepath={file.filepath || ""}
                                          attachmentId={file.attachment_id}
                                          fileType="ID_PROOF"
                                          fileName={fileName}
                                          refId={applicationData?.applicant_id}
                                          module="APPLICATION"
                                          filesList={filesList}
                                        />
                                      </div>
                                    );
                                  })()}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                </div>
              )}
            </div>

            {/* Impersonation warning - matches Angular */}
            {isImpersonate && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-sm font-semibold text-amber-800">
                Warning: All the terms and signature related information must be filled by applicant itself.
              </div>
            )}

            {/* Spanish FCRA notice - matches Angular summary-info */}
            <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-700">
              <p>Para informatión en español, visite{" "}
                <a href="https://www.consumerfinance.gov/learnmore" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                  www.consumerfinance.gov/learnmore
                </a>{" "}
                o escribe a la Consumer Financial Protection Bureau, 1700 G Street N.W., Washington, DC 20552.
              </p>
            </div>

            {/* Required Disclosures Section - subhead like Identity Verification */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-violet-600" />
                </span>
                Required Disclosures <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-red-600 font-semibold mb-3">
                You must click on all links in order to continue
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
                  <input
                    type="checkbox"
                    data-testid="signature-disclosure-1-checkbox"
                    checked={link1Checked}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        if (e.target.checked) {
                          openDisclosureModal(1);
                        } else {
                          setLink1Checked(false);
                        }
                      }
                    }}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    data-testid="signature-disclosure-1-view-btn"
                    onClick={(event) => handleDisclosureLinkClick(event, 1)}
                    className="text-left text-blue-600 hover:text-blue-800 underline text-sm flex-1 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    A Summary of Your Rights Under the Fair Credit Reporting Act
                  </button>
                </div>
                <div className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
                  <input
                    type="checkbox"
                    data-testid="signature-disclosure-2-checkbox"
                    checked={link2Checked}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        if (e.target.checked) {
                          openDisclosureModal(2);
                        } else {
                          setLink2Checked(false);
                        }
                      }
                    }}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    data-testid="signature-disclosure-2-view-btn"
                    onClick={(event) => handleDisclosureLinkClick(event, 2)}
                    className="text-left text-blue-600 hover:text-blue-800 underline text-sm flex-1 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Notice Regarding Background Investigation Pursuant to
                    California Law
                  </button>
                </div>
                <div className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
                  <input
                    type="checkbox"
                    data-testid="signature-disclosure-3-checkbox"
                    checked={link3Checked}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        if (e.target.checked) {
                          openDisclosureModal(3);
                        } else {
                          setLink3Checked(false);
                        }
                      }
                    }}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    data-testid="signature-disclosure-3-view-btn"
                    onClick={(event) => handleDisclosureLinkClick(event, 3)}
                    className="text-left text-blue-600 hover:text-blue-800 underline text-sm flex-1 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Authorization for Liberty Rent to Collect Payments
                  </button>
                </div>
              </div>
              {validateForm && fieldErrors.allDocumentsChecked && (
                <div className="mt-2 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.allDocumentsChecked}
                </div>
              )}
            </div>

            {/* Acknowledgment and Authorization Section - subhead like Identity Verification */}
            <div className="mb-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 shadow-sm">
              <label className="block text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FileSignature className="w-4 h-4 text-violet-600" />
                </span>
                Acknowledgment and Authorization
              </label>
              <p className="text-xs text-red-600 mb-4 font-semibold bg-red-50 p-2 rounded border-l-4 border-red-400">
                [IMPORTANT -- PLEASE READ CAREFULLY BEFORE SIGNING ACKNOWLEDGMENT]
              </p>
              <div className="space-y-4 text-sm text-gray-700 leading-relaxed bg-white p-4 rounded border border-gray-200">
                <p>
                  I acknowledge receipt of the DISCLOSURE REGARDING BACKGROUND INVESTIGATION and A SUMMARY OF YOUR RIGHTS UNDER THE FAIR CREDIT REPORTING ACT and certify that I have read and understand both of those documents.
                </p>
                <p>
                  I hereby authorize the obtaining of consumer reports and/or
                  investigative consumer reports at any time after receipt of this
                  authorization and, if I am approved for residency, throughout my
                  tenancy.
                </p>
                <p>
                  To this end, I hereby authorize, without reservation, any law
                  enforcement agency, administrator, state or federal agency,
                  institution, school or university (public or private),
                  information service bureau, employer, or insurance company to
                  furnish any and all background information requested by{" "}
                  <strong>
                    {bureauName}, {bureauAddress}, Phone: {bureauPhone}
                    {bureauFax ? `, Fax: ${bureauFax}` : ""}
                    {bureauWeb && (
                      <>, Web:{" "}
                        <a
                          href={bureauWeb}
                          className="text-blue-600 hover:text-blue-800 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bureauWeb}
                        </a>
                      </>
                    )}
                  </strong>{" "}
                  {isEquifax && "(Look for the Free Credit Report, Lifting Freezes, Correcting or Disputing your credit information), "}
                  another outside organization
                  acting on behalf of the Company, and/or the Company itself.
                </p>
                <p>
                  I agree that an electronic or photographic copy of this
                  Authorization shall be as valid as the original.
                </p>
                <p>
                  I understand that by typing my name and the last four digits of
                  my Social Security Number or User ID, and clicking on the APPLICANT_CONTENT.application.buttons.next
                  button below, constitutes my electronic signature, dated as of
                  when I click on the "Sign Application" button, and that by doing
                  so:
                </p>
              </div>

            {/* Certifications */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-900 mb-3">
                Certifications:
              </p>
              <div className="space-y-2.5 text-sm text-gray-700">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I am authorizing {bureauName} to conduct
                    the background check(s) described above
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I am consenting to use electronic means to sign this form and
                    have read and understand the above disclosure
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify I have read and understand the Summary Of Your
                    Rights Under The Fair Credit Reporting Act
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify I have read and understand the Notice Regarding
                    Background Investigation Pursuant To California Law
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify that the information submitted is correct and true.
                    I understand that providing false or tampered with
                    documentation will result in a denial of my application.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify I have read, agree to the Authorization for Liberty
                    Rent to Collect Payments.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    The applicant expressly gives permission to applicant property
                    to share personal information with Liberty Rent through the
                    life of the lease contract.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I understand that my Liberty Rent Application Fee is
                    Non-Refundable, unless I am Denied by Liberty Rent.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I understand that the Liberty Rent Certificate Fee is
                    Non-Refundable.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I acknowledge I may request a hard copy of this Disclosure and
                    Authorization form after agreeing to the background check
                    electronically by emailing Liberty Rent at Email:{" "}
                    <a
                      href="mailto:help@libertyrent.com"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      help@libertyrent.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Consent Text */}
            <div className="mb-6 space-y-4">
              <p className="text-sm text-gray-700">
                I hereby consent to receive disclosures and information
                electronically on this website and to electronically sign
                documents.
              </p>
              <p className="text-sm text-gray-700">
                If you do not wish to complete and sign this application
                electronically, please contact us for instructions
              </p>
            </div>

            {/* SSN or ITIN */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                SSN or ITIN <span className="text-red-500">*</span>
              </label>
              <SSNInput
                value={ssnDigits}
                onChange={(value) => {
                  setSsnDigits(value);
                  if (fieldErrors.ssn) {
                    setFieldErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.ssn;
                      return newErrors;
                    });
                  }
                }}
                disabled={true}
                placeholder="XXX-XX-XXXX"
                className={`w-full max-w-md px-3 py-2 border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent ${validateForm && fieldErrors.ssn
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300"
                  }`}
              />
              {validateForm && fieldErrors.ssn && (
                <div className="mt-1 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.ssn}
                </div>
              )}
            </div>

            {/* Access Statement - "I have read and agree with these statements" - just prior to signature */}
            <div className="mb-6 space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="access_statement"
                  checked={accessStatementChecked}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      setAccessStatementChecked(e.target.checked);
                      if (e.target.checked && fieldErrors.accessStatement) {
                        setFieldErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.accessStatement;
                          return newErrors;
                        });
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className={`mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 ${isReadOnly
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                    }`}
                />
                <label
                  htmlFor="access_statement"
                  className={`text-sm leading-relaxed ${isReadOnly
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 cursor-pointer"
                    }`}
                >
                  I have read and agree with these statements
                </label>
              </div>
              {validateForm && fieldErrors.accessStatement && (
                <div className="ml-7 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.accessStatement}
                </div>
              )}
            </div>

            {/* Signature */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Signature <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                By completing this field, the applicant consents to electronically
                signing this application with Liberty Rent
              </p>
              {!allDocumentsChecked && (
                <p className="text-xs text-amber-600 mb-2 font-medium">
                  Please check all required documents above to enable signature
                  field.
                </p>
              )}
              {allDocumentsChecked && !accessStatementChecked && (
                <p className="text-xs text-amber-600 mb-2 font-medium">
                  Please check &quot;I have read and agree with these statements&quot; above to enable signature field.
                </p>
              )}
              <input
                type="text"
                value={signature}
                onChange={(e) => {
                  const value = e.target.value;
                  setSignature(value);
                  // Clear errors when user starts typing (but keep modal open if it's showing)
                  if (fieldErrors.signature) {
                    setFieldErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.signature;
                      return newErrors;
                    });
                  }
                  if (signatureError) {
                    setSignatureError("");
                  }
                }}
                disabled={isReadOnly || !allDocumentsChecked || !accessStatementChecked}
                placeholder={
                  allDocumentsChecked && accessStatementChecked
                    ? `Enter: ${getExpectedSignature() || "First and Last Name"}`
                    : !allDocumentsChecked
                      ? "Check all required documents first"
                      : "Check \"I have read and agree with these statements\" first"
                }
                className={`w-full max-w-md px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${validateForm && fieldErrors.signature
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300"
                  } ${!allDocumentsChecked || !accessStatementChecked
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white"
                  }`}
              />
              {validateForm && fieldErrors.signature && (
                <div className="mt-1 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.signature}
                </div>
              )}
            </div>

            {/* Agreement Detail - shown when access_statement is checked and signature_date exists */}
            {String(applicationData?.access_statement) === "1" &&
              applicationData?.signature_date && (
                <div className="mt-4 mb-6">
                  <p className="text-sm font-bold text-gray-900">
                    Agreement Detail: Agreed on{" "}
                    {new Date(
                      typeof applicationData.signature_date === "string" &&
                        !isNaN(Number(applicationData.signature_date))
                        ? Number(applicationData.signature_date) * 1000
                        : applicationData.signature_date,
                    )
                      .toLocaleString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      })
                      .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")}{" "}
                    from {applicationData?.ip_address || "N/A"}
                  </p>
                </div>
              )}

            </div>

          </div>

          {/* Save Message - Only show success messages */}
          {saveMessage && saveMessage.type === "success" && (
            <div className="mb-4 p-4 rounded-lg flex items-start gap-3 bg-green-50 border border-green-200">
              <AlertTriangle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  {saveMessage.text}
                </p>
              </div>
              <button
                data-testid="signature-save-message-dismiss-button"
                onClick={() => setSaveMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <SignatureDisclosureModal
          linkId={disclosureModalLinkId}
          creditBureauConfig={creditBureauConfig}
          isReadOnly={isReadOnly}
          onClose={closeDisclosureModal}
          onAccept={handleDisclosureAccept}
        />

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 pb-6">
          <button
            data-testid="signature-back-button"
            type="button"
            onClick={() => {
              const historyPath = applicantId
                ? getTabPathWithApplicantId("history", applicantId)
                : "/applicant/application/history";
              if (applicantId) {
                navigate(historyPath, getTabNavigationOptions("history"));
              } else {
                navigate(historyPath);
              }
            }}
            disabled={isSaving || isSavingExit || uploadingGovId}
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {APPLICANT_CONTENT.application.buttons.back}
          </button>
          {!isReadOnly && (
            <button
              data-testid="signature-save-continue-button"
              onClick={() => handleSaveAndContinue()}
              disabled={
                isSaving ||
                uploadingGovId ||
                isSavingExit ||
                !applicationData?.applicant_id
              }
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving || uploadingGovId ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {uploadingGovId ? "Uploading..." : APPLICANT_CONTENT.application.messages.saving}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {APPLICANT_CONTENT.application.buttons.saveContinue}
                </>
              )}
            </button>
          )}
          {isReadOnly && (
            <button
              data-testid="signature-next-button"
              onClick={() => {
                const paymentPath = applicantId
                  ? getTabPathWithApplicantId("payment", applicantId)
                  : "/applicant/application/payment";
                if (applicantId) {
                  navigate(paymentPath, getTabNavigationOptions("payment"));
                } else {
                  navigate(paymentPath);
                }
              }}
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              {APPLICANT_CONTENT.applyNow.step3.nextButton}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="signature-save-exit-button"
            onClick={() => {
              if (isReadOnly) {
                navigate("/applicant/dashboard", { replace: true });
              } else {
                handleSaveAndContinue(true);
              }
            }}
            disabled={
              !isReadOnly &&
              (isSavingExit ||
                isSaving ||
                uploadingGovId ||
                !allDocumentsChecked ||
                !applicationData?.applicant_id)
            }
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingExit ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : isReadOnly ? (
              APPLICANT_CONTENT.application.buttons.exit
            ) : (
              APPLICANT_CONTENT.application.buttons.saveExit
            )}
          </button>
        </div>
      </div>

      {/* Validation Modal */}
      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showValidationModal}
        title={APPLICANT_CONTENT.errors.missingFields}
        message={validationModalMessage}
        showValidationIcon={validationShowIcon}
        onClose={() => setShowValidationModal(false)}
        overlayDataTestId={
          validationShowIcon ? undefined : "signature-validation-error-modal"
        }
        closeButtonDataTestId={
          validationShowIcon
            ? undefined
            : "signature-validation-error-modal-close-button"
        }
        confirmButtonDataTestId={
          validationShowIcon
            ? undefined
            : "signature-validation-error-modal-ok-button"
        }
      />
      </div>
    </div>
  );
}
